import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { LanguageSwitcher } from '../shared/LanguageSwitcher';
import { ROUTES, EXTERNAL_LINKS, ASSETS } from '../../constants';

export const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isFinanceOpen, setIsFinanceOpen] = useState(false);
  const { t } = useLanguage();
  const location = useLocation();

  const navLinks = [
    { path: ROUTES.HOME, label: t('nav.home') },
    {
      path: ROUTES.FINANCE,
      label: t('nav.finance'),
      submenu: [
        { path: ROUTES.BLUE_STRIKE, label: 'Blue Strike' },
        { path: ROUTES.ALGO_ENGINE, label: 'Algo Engine 2.0' },
        { path: ROUTES.COPY_SERVICE, label: 'Copy Service' },
      ]
    },
    { path: ROUTES.REAL_ESTATE, label: t('nav.realEstate') },
    { path: ROUTES.TOKEN, label: t('nav.token') },
    { path: ROUTES.AI_AUTOMATION, label: t('nav.aiAutomation') },
    { path: ROUTES.CORPORATE_INFO, label: t('nav.infoSocietarie') },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B1120] border-b border-gray-800 shadow-md animate-slide-down">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-28">

          {/* Logo Section */}
          <Link to={ROUTES.HOME} className="flex items-center space-x-3 mr-4 sm:mr-8 group shrink-0">
            <img
              src={ASSETS.LOGO_COMPANY}
              alt="ChartTower Logo"
              className="w-28 h-28 sm:w-32 sm:h-32 object-contain transition-transform duration-300 group-hover:scale-110 drop-shadow-lg"
            />
            <span className="hidden sm:block font-bold text-xl sm:text-2xl tracking-tight text-gray-300 transition-all duration-300 group-hover:text-white group-hover:drop-shadow-[0_0_10px_rgba(249,115,22,0.8)]">
              CHART<span className="text-orange-500 group-hover:text-orange-400">TOWER</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-center flex-1 space-x-4 xl:space-x-8">
            {navLinks.map((link) => (
              <div key={link.label} className="relative group">
                <Link
                  to={link.path}
                  className={`text-sm xl:text-base font-medium transition-colors duration-200 hover:scale-105 whitespace-nowrap flex items-center gap-1 ${isActive(link.path) ? 'text-white' : 'text-gray-400 hover:text-white'
                    }`}
                >
                  {link.label}
                  {link.submenu && <ChevronDown size={16} className="group-hover:rotate-180 transition-transform" />}
                </Link>

                {/* Desktop Dropdown */}
                {link.submenu && (
                  <div className="absolute top-full left-0 mt-2 w-48 bg-[#151E32] border border-gray-700 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                    <div className="py-2">
                      {link.submenu.map((sublink) => (
                        <Link
                          key={sublink.path}
                          to={sublink.path}
                          className="block px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
                        >
                          {sublink.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Actions Section - Desktop */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-6">
            <LanguageSwitcher />

            <a
              href={EXTERNAL_LINKS.TELEGRAM_ACCESS}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2.5 px-5 rounded-full transition-all duration-300 shadow-lg hover:shadow-orange-500/30 hover:-translate-y-0.5 whitespace-nowrap"
            >
              {t('nav.community')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-3 sm:space-x-4">
            <LanguageSwitcher />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 transition-colors focus:outline-none"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-[#0B1120] border-b border-gray-800 animate-slide-down max-h-[calc(100vh-7rem)] overflow-y-auto shadow-2xl backdrop-blur-xl">
          <div className="px-4 pt-2 pb-6 space-y-1">
            {navLinks.map((link) => (
              <div key={link.label}>
                {link.submenu ? (
                  <>
                    {/* Accordion Trigger */}
                    <button
                      onClick={() => setIsFinanceOpen(!isFinanceOpen)}
                      className={`w-full flex items-center justify-between px-3 py-3 rounded-md text-base font-medium transition-colors ${isActive(link.path) ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                        }`}
                    >
                      <span>{link.label}</span>
                      <ChevronDown size={20} className={`transition-transform ${isFinanceOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Accordion Content */}
                    {isFinanceOpen && (
                      <div className="ml-4 mt-1 space-y-1">
                        {link.submenu.map((sublink) => (
                          <Link
                            key={sublink.path}
                            to={sublink.path}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="block px-3 py-2 rounded-md text-sm text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
                          >
                            {sublink.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block px-3 py-3 rounded-md text-base font-medium transition-colors ${isActive(link.path) ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                      }`}
                  >
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
            <div className="pt-4 px-3">
              <a
                href={EXTERNAL_LINKS.TELEGRAM_ACCESS}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-4 rounded-full transition-colors shadow-lg"
              >
                {t('nav.community')}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
